import { observable, action, computed } from 'mobx';

import { isRequired, BaseFormModel, displayName } from 'stores/BaseForm';
import {
  TaskType,
  IDataset,
  TaskViewDto,
  CreateTaskReqParams,
  ArchitectureType,
  MLModel,
} from 'stores/Recognition/types';
import AppRouter from 'stores/AppRouter';

import Log from 'helpers/log';
import httpFacade from 'http/httpFacade';

interface TaskParams {
  detectorId: string;
  classifierId: string;
  datasetId: string;
}

class TaskEvalDetectorClassifierForm extends BaseFormModel {
  type: TaskType.EVAL_MODEL_DETECTOR_DVC;

  @observable
  @displayName('DESCRIPTION*')
  @isRequired()
  description: string;

  @observable
  @displayName('DETECTOR_MODEL')
  @isRequired()
  detectorId: string;

  @observable
  @displayName('CLASSIFICATOR_MODEL')
  @isRequired()
  classifierId: string;

  @observable
  @displayName('*DATASET')
  @isRequired()
  datasetId: string;

  @observable loading = false;

  @observable datasets: IDataset[] = [];
  @observable tasks: TaskViewDto[] = [];
  @observable models: MLModel[] = [];

  @computed
  get datasetsOptions() {
    return this.datasets.map(it => ({
      value: it.id,
      label: it.name,
    }));
  }

  @computed
  get modelsClassificationOptions() {
    return this.models
      .filter(
        model => model.architecture.type === ArchitectureType.CLASSIFICATION,
      )
      .map(it => ({
        value: it.id,
        label: it.description,
      }));
  }

  @computed
  get modelsDetectionOptions() {
    return this.models
      .filter(model => model.architecture.type === ArchitectureType.DETECTION)
      .map(it => ({
        value: it.id,
        label: it.description,
      }));
  }

  constructor(type: TaskType.EVAL_MODEL_DETECTOR_DVC) {
    super();

    this.type = type;
  }

  @computed
  get requestParams(): CreateTaskReqParams<TaskParams> {
    return {
      type: this.type,
      description: this.description,
      params: {
        classifierId: this.classifierId,
        detectorId: this.detectorId,
        datasetId: this.datasetId,
      },
    };
  }

  @action.bound
  async init() {
    try {
      this.loading = true;

      const [datasets, tasks, models] = await Promise.all([
        httpFacade.recognition.fetchDatasets(),
        httpFacade.recognition.fetchTasks(),
        httpFacade.recognition.fetchModels(),
      ]);

      this.datasets = datasets.data;
      this.tasks = tasks.data;
      this.models = models.data;
    } catch (error) {
      Log.info(error);
    } finally {
      this.loading = false;
    }
  }

  @action.bound
  async onCreate() {
    try {
      this.loading = true;

      await httpFacade.recognition.createTask(this.requestParams);

      AppRouter.goBack();
    } catch (error) {
      Log.info(error);
    } finally {
      this.loading = false;
    }
  }
}

export default TaskEvalDetectorClassifierForm;
